import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { apiErrorMessage } from '../api/referralsApi';
import { useReferralMutations, useReferralSettings, useReferralTemplate } from '../hooks';
import { ReasonChips } from '../components';
import chips from '../components/chips.module.css';
import styles from './ReferralSettingsPage.module.css';

/**
 * "Setup your form": what referring offices see when they pick this location, who gets notified,
 * the tooth numbering on this location's slips and how long before an unopened slip is nudged.
 */
export function ReferralSettingsPage() {
  const toast = useToast();
  const settings = useReferralSettings();
  const template = useReferralTemplate(null);
  const { saveSettings } = useReferralMutations();
  const [form, setForm] = useState({
    customReasons: [] as string[],
    notifyEmails: [] as string[],
    acceptingNewPatients: true,
    intakeInstructions: '',
    toothNumbering: 'universal' as 'universal' | 'fdi',
    reminderAfterHours: 48,
    unconfirmedAfterDays: 7,
    patientFollowUp: true,
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!settings.data || loaded) return;
    setForm({
      customReasons: settings.data.customReasons,
      notifyEmails: settings.data.notifyEmails,
      acceptingNewPatients: settings.data.acceptingNewPatients,
      intakeInstructions: settings.data.intakeInstructions ?? '',
      toothNumbering: settings.data.toothNumbering,
      reminderAfterHours: settings.data.reminderAfterHours,
      unconfirmedAfterDays: settings.data.unconfirmedAfterDays,
      patientFollowUp: settings.data.patientFollowUp,
    });
    setLoaded(true);
  }, [settings.data, loaded]);

  const save = async () => {
    try {
      await saveSettings.mutateAsync({
        customReasons: form.customReasons,
        notifyEmails: form.notifyEmails,
        acceptingNewPatients: form.acceptingNewPatients,
        intakeInstructions: form.intakeInstructions.trim() || null,
        toothNumbering: form.toothNumbering,
        reminderAfterHours: form.reminderAfterHours,
        unconfirmedAfterDays: form.unconfirmedAfterDays,
        patientFollowUp: form.patientFollowUp,
      });
      toast.success('Referral form settings saved.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the settings'));
    }
  };

  if (settings.isLoading || !loaded) {
    return (
      <div className={patterns.page}>
        <Skeleton height={80} />
        <Skeleton height={300} />
      </div>
    );
  }

  const isDental = template.data?.vertical !== 'medical';

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Setup your referral form"
        subtitle="What other offices see when they refer to you, and how your own slips behave."
        actions={
          <Link to="/referrals" className={styles.back}>
            <ArrowLeft size={14} /> Back to referrals
          </Link>
        }
      />

      <Card title="Receiving referrals" subtitle="Shown to offices that pick your practice on their slip.">
        <div className={patterns.fieldStack}>
          <label className={styles.check}>
            <input type="checkbox" checked={form.acceptingNewPatients} onChange={(e) => setForm((f) => ({ ...f, acceptingNewPatients: e.target.checked }))} />
            Accepting new patients
          </label>
          <label className={patterns.field}>
            <span className={patterns.fieldLabel}>Intake instructions (optional)</span>
            <textarea
              className={patterns.textarea}
              rows={3}
              value={form.intakeInstructions}
              onChange={(e) => setForm((f) => ({ ...f, intakeInstructions: e.target.value }))}
              placeholder="Please include a recent panoramic X-ray. We see emergencies same day — call the office directly."
              maxLength={2000}
            />
          </label>
          <div className={patterns.field}>
            <span className={patterns.fieldLabel}>Reasons for referral you want offered on slips sent to you</span>
            <p className={styles.hint}>
              Your specialty's common reasons are always shown. Add the ones specific to your office.
            </p>
            <ReasonChips options={[]} selected={form.customReasons} onChange={(customReasons) => setForm((f) => ({ ...f, customReasons }))} />
          </div>
        </div>
      </Card>

      <Card title="Notifications" subtitle="Who hears about referrals besides the person who sent one.">
        <div className={patterns.field}>
          <span className={patterns.fieldLabel}>Also email</span>
          <p className={styles.hint}>New referrals you receive, and status updates and reports on referrals you sent.</p>
          <EmailChips emails={form.notifyEmails} onChange={(notifyEmails) => setForm((f) => ({ ...f, notifyEmails }))} />
        </div>
      </Card>

      <Card title="Your slips" subtitle="How referrals you send behave.">
        <div className={styles.row}>
          {isDental && (
            <Select
              label="Tooth numbering"
              fullWidth
              options={[
                { value: 'universal', label: 'Universal (1–32, A–T)' },
                { value: 'fdi', label: 'FDI (11–48, 51–85)' },
              ]}
              value={form.toothNumbering}
              onChange={(e) => setForm((f) => ({ ...f, toothNumbering: e.target.value as 'universal' | 'fdi' }))}
            />
          )}
          <Select
            label="Remind the receiving office if unopened after"
            fullWidth
            options={[
              { value: '0', label: 'Never' },
              { value: '24', label: '1 day' },
              { value: '48', label: '2 days' },
              { value: '72', label: '3 days' },
              { value: '168', label: '1 week' },
            ]}
            value={String(form.reminderAfterHours)}
            onChange={(e) => setForm((f) => ({ ...f, reminderAfterHours: Number(e.target.value) }))}
          />
          <Select
            label="Flag a referral nobody confirmed after"
            fullWidth
            options={[
              { value: '0', label: 'Never' },
              { value: '3', label: '3 days' },
              { value: '5', label: '5 days' },
              { value: '7', label: '1 week' },
              { value: '10', label: '10 days' },
              { value: '14', label: '2 weeks' },
            ]}
            value={String(form.unconfirmedAfterDays)}
            onChange={(e) => setForm((f) => ({ ...f, unconfirmedAfterDays: Number(e.target.value) }))}
          />
        </div>
        <p className={styles.hint}>
          When the window passes with no office confirming a booking, the referral shows under Needs action and your notify list gets one
          email, so the front desk can record where the patient went in one tap.
        </p>
        <label className={styles.check}>
          <input type="checkbox" checked={form.patientFollowUp} onChange={(e) => setForm((f) => ({ ...f, patientFollowUp: e.target.checked }))} />
          <span>
            <strong>Check in with the patient by text a week after sending</strong>: "Did you book with 1, 2 or 3, or not yet?" Their reply
            closes the loop, or flags the referral when nobody has confirmed. New slips default to this; it needs your texting number live.
          </span>
        </label>
      </Card>

      <div className={styles.actions}>
        <Button onClick={() => void save()} loading={saveSettings.isPending}>
          Save settings
        </Button>
      </div>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A list of addresses as chips; type one and press Enter (or Add) to put it on the list. */
function EmailChips({ emails, onChange }: { emails: string[]; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const commit = () => {
    const value = draft.trim().toLowerCase();
    if (!value) return;
    if (!EMAIL_RE.test(value)) {
      setError('That does not look like an email address.');
      return;
    }
    if (!emails.includes(value)) onChange([...emails, value]);
    setDraft('');
    setError(null);
  };
  return (
    <div className={styles.emailList}>
      {emails.length > 0 && (
        <div className={chips.row}>
          {emails.map((email) => (
            <span key={email} className={styles.emailChip}>
              {email}
              <button type="button" className={styles.emailRemove} aria-label={`Remove ${email}`} onClick={() => onChange(emails.filter((e) => e !== email))}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className={styles.emailAdd}>
        <Input
          type="email"
          fullWidth
          value={draft}
          placeholder={emails.length ? 'Add another address' : 'frontdesk@yourpractice.com'}
          error={error ?? undefined}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit();
            }
          }}
          onBlur={() => draft.trim() && commit()}
        />
        <Button type="button" variant="secondary" size="sm" leftIcon={<Plus size={13} />} onClick={commit} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
      {emails.length === 0 && <p className={styles.hint}>Nobody besides the sender yet. Press Enter after each address.</p>}
    </div>
  );
}
